import React, { useState, useRef, useEffect } from 'react'
import { Modal, Upload, Carousel, message, Button, Tooltip, Spin, Popover } from 'antd'
import { PlusOutlined, LeftOutlined, RightOutlined, FileTextOutlined } from '@ant-design/icons'
import '../../styles/UploadOrderPhoto.scss'
import { uploadPhoto, convertDownloadLink } from '../../../../services/api/EntranceServices'
import { getAccessToken } from '../../../../services/AuthServices'

function UploadOrderPhoto(props) {
    const { item = {}, reloadItems } = props
    const { photo_request = {} } = item || {}
    const { _id: requestId, related_data: requestRelatedData = {} } = photo_request || {}
    const { note = '' } = requestRelatedData.requested || {}

    const [loading, setLoading] = useState(false)
    const [requestInfo, setRequestInfo] = useState(photo_request || {})
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [fileList, setFileList] = useState([])
    const [activeIndex, setActiveIndex] = useState(0)
    const [animating, setAnimating] = useState(false)
    const [isSliding, setIsSliding] = useState(false)
    const sliderRef = useRef(null)
    const uploadListRef = useRef(null)
    const messageShownRef = useRef(new Set())

    const pendingFilesRef = useRef([]) // chứa các file đang chờ xử lý
    const debounceTimerRef = useRef(null)
    const ANIM_DURATION = 450

    const showMessageOnce = (msg) => {
        if (!messageShownRef.current.has(msg)) {
            message.error(msg)
            messageShownRef.current.add(msg)
        }
    }

    const handleShowModal = () => {
        const { related_data = {} } = photo_request || {}
        const { photos = [] } = related_data.updated || {}
        const initialFiles = photos.length ? photos.map((photo, idx) => ({
            uid: `image-uid${idx}`,
            name: `image-${idx}`,
            status: 'done',
            url: photo.url,
            size: photo.size,
            type: photo.mimetype,
        })) : []
        setRequestInfo(photo_request || {})
        setFileList(initialFiles)
        setIsModalOpen(true)
    }

    const handleCancel = () => {
        setIsModalOpen(false)
        setFileList([])
        setRequestInfo({})
        setActiveIndex(0)
        reloadItems()
    }

    const resetMessages = () => {
        messageShownRef.current = new Set()
    }

    const getFileMime = (f) => f.type || f.originFileObj?.type || ''
    const isImageFile = (f) => getFileMime(f).startsWith('image/')
    const isVideoFile = (f) => getFileMime(f).startsWith('video/')

    // Đuôi file media hợp lệ + map mime subtype -> đuôi chuẩn
    const MEDIA_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg', 'ico', 'mp4', 'webm', 'ogg', 'mov', 'mkv', 'avi', 'mp3']
    const MIME_EXT_MAP = {
        jpeg: 'jpg',
        'svg+xml': 'svg',
        'x-icon': 'ico',
        quicktime: 'mov',
        'x-matroska': 'mkv',
        'x-msvideo': 'avi',
        mpeg: 'mp3',
    }

    const getFileExt = (f) => {
        // Ưu tiên lấy đuôi từ mime type (cắt bỏ param vd "image/jpeg; charset=...")
        const type = (getFileMime(f) || '').split(';')[0].trim().toLowerCase()
        if (type && type.includes('/')) {
            const sub = type.split('/')[1]
            if (sub) {
                // Map subtype đặc biệt, mặc định cắt phần sau dấu '+' (vd: svg+xml -> svg)
                return MIME_EXT_MAP[sub] || sub.split('+')[0]
            }
        }
        // Nếu không có type, parse đuôi từ URL (bỏ query string), chỉ nhận đuôi hợp lệ
        const url = ((f && (f.url || f.preview)) || '').split('?')[0]
        const match = url.match(/\.([a-zA-Z0-9]{2,5})(?:\/[^/]*)?$/)
        if (match && MEDIA_EXTENSIONS.includes(match[1].toLowerCase())) return match[1].toLowerCase()
        return 'jpg'
    }

    const validateAndAddFiles = (newFiles) => {
        const MAX_PHOTOS = 20
        const MAX_VIDEOS = 10
        const MAX_PHOTO_SIZE = 10 * 1024 * 1024 // 10MB
        const MAX_VIDEO_SIZE = 500 * 1024 * 1024 // 500MB
        resetMessages()

        setFileList(prev => {
            const currentFiles = prev.filter(f => f.status !== 'error')
            const validFiles = []
            let photoCount = currentFiles.filter(isImageFile).length
            let videoCount = currentFiles.filter(isVideoFile).length

            for (const file of newFiles) {
                const mime = file.type || ''
                const isImage = mime.startsWith('image/')
                const isVideo = mime.startsWith('video/')
                const size = file.size || 0

                if (!isImage && !isVideo) {
                    showMessageOnce('Chỉ được upload file ảnh hoặc video.')
                    continue
                }

                if (isImage) {
                    if (photoCount >= MAX_PHOTOS) {
                        showMessageOnce(`Chỉ được upload tối đa ${MAX_PHOTOS} ảnh.`)
                        continue
                    }
                    if (size > MAX_PHOTO_SIZE) {
                        showMessageOnce('Mỗi ảnh không vượt quá 10MB.')
                        continue
                    }
                    photoCount++
                } else {
                    if (videoCount >= MAX_VIDEOS) {
                        showMessageOnce(`Chỉ được upload tối đa ${MAX_VIDEOS} video.`)
                        continue
                    }
                    if (size > MAX_VIDEO_SIZE) {
                        showMessageOnce('Mỗi video không vượt quá 500MB.')
                        continue
                    }
                    videoCount++
                }

                validFiles.push(file)
            }

            const mappedFiles = validFiles.map(file => {
                const preview = URL.createObjectURL(file)
                return {
                    uid: file.uid,
                    name: file.name,
                    status: 'done',
                    url: preview,
                    thumbUrl: preview,
                    type: file.type,
                    originFileObj: file,
                }
            })

            return [...currentFiles, ...mappedFiles]
        })
    }

    // Chặn upload tự động, đồng thời kiểm tra tổng dung lượng
    // antd 4.5.4 chưa hỗ trợ Upload.LIST_IGNORE; return false để antd không upload
    const beforeUpload = (file, fileListFromEvent) => {
        pendingFilesRef.current.push(file)

        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current)
        }

        debounceTimerRef.current = setTimeout(() => {
            const pending = [...pendingFilesRef.current]
            pendingFilesRef.current = []
            validateAndAddFiles(pending)
        }, 100)

        return false
    }

    const highlightThumbnail = (index) => {
        const container = uploadListRef.current
        if (container) {
            const items = container.querySelectorAll('.ant-upload-list-item')
            items.forEach((item, idx) => {
                idx === index
                    ? item.classList.add('active-thumbnail')
                    : item.classList.remove('active-thumbnail')
            })
        }
    }

    const handlePreview = (file) => {
        if (isSliding) return
        const idx = fileList.findIndex((f) => f.uid === file.uid)
        if (sliderRef.current && sliderRef.current.goTo) {
            setIsSliding(true)
            sliderRef.current.goTo(idx, true)
            setActiveIndex(idx)
            setTimeout(() => setIsSliding(false), ANIM_DURATION)
        }
        setIsModalOpen(true)
    }

    const handleBeforeChange = () => {
        // setAnimating(true)
    }

    const handleAfterChange = (current) => {
        setActiveIndex(current)
        setAnimating(false)
    }

    useEffect(() => {
        highlightThumbnail(activeIndex)
    }, [activeIndex, fileList])

    const handlePrev = () => {
        if (!sliderRef.current || animating || isSliding) return
        const len = fileList.length
        const newIndex = (activeIndex - 1 + len) % len
        setIsSliding(true)
        sliderRef.current.goTo(newIndex, true)
        setActiveIndex(newIndex)
        setTimeout(() => setIsSliding(false), ANIM_DURATION)
    }

    const handleNext = () => {
        if (!sliderRef.current || animating || isSliding) return
        const len = fileList.length
        const newIndex = (activeIndex + 1) % len
        setIsSliding(true)
        sliderRef.current.goTo(newIndex, true)
        setActiveIndex(newIndex)
        setTimeout(() => setIsSliding(false), ANIM_DURATION)
    }

    const handleUpload = async (files) => {
        setLoading(true)
        try {
            if (!Array.isArray(files)) return
            const formData = new FormData()
            files.forEach(fileObj => {
                const fileItem = fileObj.originFileObj || fileObj
                // append file (FormData sẽ tự detect Content-Type)
                formData.append('photos', fileItem)
            })
            // Thêm trường source
            formData.append('source', 'MCVN')
            const { success, message: mess } = await uploadPhoto(requestId, formData)
            if (!success) throw new Error(mess)
            message.success(`Upload ảnh thành công!`)
            reloadItems()
            setIsModalOpen(false)
            setFileList([])
            setRequestInfo({})
            setActiveIndex(0)
        } catch (error) {
            message.error(error.message)
        } finally {
            setLoading(false)
        }
    }
    const handleDownload = async (file, idx) => {
        const fileUrl = file.preview || file.url
        const ext = getFileExt(file)
        let fileName = file.name
        if (!fileName) {
            fileName = `media-${idx}.${ext}`
        } else {
            // Chỉ coi là "đã có đuôi" khi đuôi hiện tại là đuôi media hợp lệ
            // (tránh false-positive với tên chứa dấu chấm như "report.2024")
            const currentExt = (fileName.match(/\.([a-zA-Z0-9]{2,5})$/) || [])[1]
            if (!currentExt || !MEDIA_EXTENSIONS.includes(currentExt.toLowerCase())) {
                fileName = `${fileName}.${ext}`
            }
        }
        const fullUrl = convertDownloadLink(fileUrl)

        try {
            const resp = await fetch(fullUrl, {
                headers: {
                    'Authorization': `Bearer ${getAccessToken()}`,
                },
            })
            if (!resp.ok) throw new Error(`Status ${resp.status}`)
            const blob = await resp.blob()
            const blobUrl = URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.href = blobUrl
            a.download = fileName
            document.body.appendChild(a)
            a.click()
            a.remove()
            URL.revokeObjectURL(blobUrl)
        } catch (err) {
            console.error(err)
            message.error('Tải ảnh thất bại')
        }
    }

    const handleRemoveFile = async (e, file, idx) => {
        e.stopPropagation()
        const newList = fileList.filter(f => f.uid !== file.uid)
        setFileList(newList)
        const oldActive = activeIndex
        const removedIdx = idx
        const newLength = newList.length
        const maxIndexAfter = newLength - 1

        let newActive
        if (newLength <= 0) {
            newActive = 0
        } else if (removedIdx < oldActive) {
            newActive = Math.max(oldActive - 1, 0)
        } else if (removedIdx === oldActive) {
            newActive = Math.min(oldActive, maxIndexAfter)
        } else {
            newActive = oldActive
        }
        if (newActive !== oldActive) {
            if (sliderRef.current) {
                setIsSliding(true)
                sliderRef.current.goTo(newActive, false)
                setActiveIndex(newActive)
                setTimeout(() => setIsSliding(false), ANIM_DURATION)
            } else {
                setActiveIndex(newActive)
            }
        }
    }

    const uploadButton = (
        <div>
            <PlusOutlined />
            <div style={{ marginTop: 8 }}>Upload</div>
        </div>
    )
    const { tags = [] } = item || {}
    const showUploadBtn = tags.find(i => ['photo_requested', 'photo_updated', 'photo_confirmed'].includes(i))
    const cantUpload = tags.find(i => ['photo_updated', 'photo_confirmed', 'photo_rejected'].includes(i))

    return (
        <div className="UploadOrderPhoto">
            {showUploadBtn &&
                <Tooltip title={`${cantUpload ? 'View photo' : 'Upload Order Photo'}`}>
                    <button
                        className="btnUploadImage btn btn-primary"
                        onClick={handleShowModal}
                    >
                        <i className="fa fa-picture-o" aria-hidden="true" />
                    </button>
                </Tooltip>
            }
            {showUploadBtn && !!note &&
                <Popover
                    trigger="click"
                    placement="bottom"
                    title="Photo request note"
                    content={<div className="notePopoverContent">{note}</div>}
                    overlayStyle={{ maxWidth: 420 }}
                >
                    <Tooltip title="View note">
                        <button type="button" className="btnNote btn btn-primary">
                            <FileTextOutlined />
                        </button>
                    </Tooltip>
                </Popover>
            }
            <Modal
                className='UploadOrderPhotoModal'
                title={`${cantUpload ? 'View photo' : 'Upload Order Photo'}`}
                visible={isModalOpen}
                onCancel={handleCancel}
                width={950}
                footer={!cantUpload &&
                    (<div className='d-flex w-100 align-items-center justify-content-end'>
                        <button className="reject btn btn-secondary mx-1" disabled={loading} onClick={handleCancel}>
                            Cancel
                        </button>
                        <button className="confirm btn btn-primary mx-1" disabled={loading} onClick={() => handleUpload(fileList)}>
                            Save
                        </button>
                    </div>)
                }
            >
                <div className='d-flex align-item-center' ref={uploadListRef}>
                    <div className="ant-upload-list ant-upload-list-picture-card custom-upload-list mb-3" style={{ display: 'flex', flexWrap: 'wrap' }}>
                        {fileList.map((file, idx) => (
                            <div className='ant-upload-list-picture-card-container' key={file.uid} style={{ width: 104, height: 104, position: 'relative', cursor: 'pointer' }}>
                                <div
                                    className={`ant-upload-list-item ant-upload-list-item-undefined ant-upload-list-item-list-type-picture-card ${activeIndex === idx && 'active-thumbnail'}`}
                                    style={{ pointerEvents: isSliding ? 'none' : 'auto', opacity: isSliding ? 0.6 : 1, cursor: isSliding ? 'not-allowed' : 'pointer' }}
                                    onClick={() => handlePreview(file)}
                                >
                                    <div className="ant-upload-list-item-info">
                                        <div className="ant-upload-span">
                                            {isVideoFile(file) ? (
                                                <video
                                                    src={file.thumbUrl || file.url}
                                                    style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: 4 }}
                                                    muted
                                                />
                                            ) : (
                                                <img
                                                    src={file.thumbUrl || file.url}
                                                    alt={file.name}
                                                    style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: 4 }}
                                                />
                                            )}
                                        </div>
                                    </div>
                                    <div className="ant-upload-list-item-actions">
                                        {(!cantUpload) &&
                                            <button
                                                title="Remove file"
                                                type="button"
                                                className="ant-btn ant-btn-text ant-btn-sm ant-btn-icon-only ant-upload-list-item-card-actions-btn"
                                                onClick={(e) => handleRemoveFile(e, file, idx)}
                                            >
                                                <span role="img" aria-label="delete" tabIndex="-1" className="anticon anticon-delete">
                                                    <i className='fa fa-trash' />
                                                </span>
                                            </button>
                                        }
                                    </div>
                                </div>
                            </div>
                        ))}
                        <Upload
                            className='UploadOrderPhotoUpload mr-0'
                            listType="picture-card"
                            fileList={fileList}
                            onPreview={handlePreview}
                            multiple
                            accept="image/*,video/*"
                            beforeUpload={beforeUpload}
                            showUploadList={false}
                            disabled={loading || cantUpload}
                            onRemove={(file) => {
                                setFileList(prev => prev.filter(f => f.uid !== file.uid))
                            }}
                            customRequest={() => { }}
                        >
                            {(!cantUpload && (fileList.filter(isImageFile).length < 20 || fileList.filter(isVideoFile).length < 10)) && uploadButton}
                        </Upload>
                    </div>
                </div>

                {
                    fileList.length > 0 && (
                        <div className="carousel-wrapper fixed-size" style={{ position: 'relative' }}>
                            <Button
                                className='nav-button prev'
                                type="text"
                                icon={<LeftOutlined />}
                                onClick={handlePrev}
                                disabled={isSliding}
                                style={{ position: 'absolute', top: '45%', left: 0, transform: 'translateY(-45%)', zIndex: 1, fontSize: 24 }}
                            />
                            <Carousel
                                ref={sliderRef}
                                dotPosition="bottom"
                                effect="fade"
                                beforeChange={handleBeforeChange}
                                afterChange={handleAfterChange}
                                className="UploadOrderPhotoCarousel fixed-size"
                                initialSlide={activeIndex}
                            >
                                {fileList.map((file, idx) => (
                                    <Spin spinning={isSliding} tip='Loading...'>
                                        <div
                                            key={idx}
                                            className="image-slide fixed-size"
                                            style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}
                                        >
                                            {isVideoFile(file) ? (
                                                <video
                                                    src={file.preview || file.url}
                                                    controls
                                                    style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', margin: '0 auto', height: 500 }}
                                                />
                                            ) : (
                                                <img
                                                    src={file.preview || file.url}
                                                    alt={`preview-${idx}`}
                                                    style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', margin: '0 auto', height: 500 }}
                                                />
                                            )}
                                        </div>
                                    </Spin>
                                ))}
                            </Carousel>
                            <Button
                                className='nav-button next'
                                type="text"
                                icon={<RightOutlined />}
                                onClick={handleNext}
                                disabled={isSliding}
                                style={{ position: 'absolute', top: '45%', right: 0, transform: 'translateY(-45%)', zIndex: 1, fontSize: 24 }}
                            />
                            {!!fileList.length &&
                                <div style={{ textAlign: 'center' }}>
                                    <p className='mb-0'><em>{`Order photo ${activeIndex + 1} of ${fileList.length}`}</em></p>
                                </div>
                            }
                            {/* Nút download ảnh hiện tại */}
                            {cantUpload &&
                                <div style={{ textAlign: 'center', marginTop: 16 }}>
                                    <button
                                        className="btn btn-success"
                                        onClick={() => handleDownload(fileList[activeIndex], activeIndex)}
                                    >
                                        <i className="fa fa-download" aria-hidden="true" /> Download
                                    </button>
                                </div>
                            }
                        </div>
                    )
                }
            </Modal>
        </div >
    )
}

export default UploadOrderPhoto
